<template>
  <section class="section section--tight opening">
    <div class="opening__inner">
      <p class="opening__lede">
        He is seventy-nine. He has lived in the same Italian hamlet for fifty-six of those years.
        The hamlet sits on a ridge above the marble quarries that supplied Michelangelo, and his
        front door looks out at a road called <span class="italic">Via Fantiscritti</span> — the
        name of a quarry the Romans were already cutting in the first century BC.
      </p>

      <div class="opening__split">
        <div class="opening__col">
          <p>
            His name is Dominique Stroobant. He was born in
            <a href="https://en.wikipedia.org/wiki/Dominique_Stroobant" target="_blank" rel="noopener">Antwerp on 16 March 1947</a>.
            At thirteen he decided he would be a stonemason, and he went on to study under Stan
            Hensen and Jean Guiraud at the
            <a href="https://axel-vervoordt.s3.amazonaws.com/documents/DOMINIQUE_STROOBANT_BIO-website.pdf" target="_blank" rel="noopener">École Saint-Luc</a>
            in Brussels from 1966 to 1970. In 1970 a state scholarship took him to Carrara. He has
            not lived anywhere else since.
          </p>
          <p>
            Many international sculptors came to Carrara to use its workshops — Henry Moore, Hans
            Arp, Isamu Noguchi, Jacques Lipchitz. Almost all came as visitors. Stroobant came as a
            resident. He opened his own workshop,
            <strong>Laboratorio STYLITHOS</strong>, in 1975, at <strong>Via Fantiscritti 11</strong>
            in the hamlet of Miseglia —
            <a href="http://www.museodeibozzetti.it/assets/files/mdb/collezione/laboratori/s001283.php" target="_blank" rel="noopener">catalogued among the laboratori in collezione</a>
            of the Museo dei Bozzetti in Pietrasanta. On his Belgian passport, in the profession
            field, he chose the word <strong>Steenkapper</strong>. Not <span class="italic">sculptor</span>. Not <span class="italic">artist</span>.
          </p>
        </div>
        <aside class="opening__aside">
          <img
            src="/img/3978a6-libreria-casa-stroobant-miseglia.webp"
            alt="Inside Stroobant's house in Miseglia — his own bookshelves"
            class="opening__aside-img"
          />
          <div class="opening__aside-cap">
            Inside the Stroobant house in Miseglia. The shelves he stares at when he isn't carving.<br />
            <span class="muted">Wikimedia Commons · CC BY-SA 4.0</span>
          </div>
        </aside>
      </div>

      <div class="opening__split opening__split--reverse">
        <aside class="opening__aside">
          <img
            src="/img/babcc7-la-cava-di-marmo-fantiscritti-panoramio.webp"
            alt="Canale di Fantiscritti — the Roman-era marble quarry just above his studio"
            class="opening__aside-img"
          />
          <div class="opening__aside-cap">
            Canale di Fantiscritti — the Roman-era quarry above his studio.<br />
            <span class="muted">Wikimedia Commons · CC BY-SA 3.0</span>
          </div>
        </aside>
        <div class="opening__col">
          <p>
            Over the next five decades he carved
            <a href="https://de.wikipedia.org/wiki/Kontinuit%C3%A4t_(Skulptur)" target="_blank" rel="noopener">the world's heaviest granite Möbius loop</a>
            for Max Bill; he invented a way to photograph the sun's path across six months on a
            single sheet of film and got a
            <a href="https://pinholeresource.com/pinhole-journal-55-back-issues/" target="_blank" rel="noopener">whole issue of the Pinhole Journal</a>
            dedicated to him; he directed seven art students through carving a marble bas-relief
            of the executed Spanish anarchist Francisco Ferrer i Guàrdia; and he is invisible in
            <a href="https://www.degruyterbrill.com/document/doi/10.1515/dmvm-1999-0410/pdf" target="_blank" rel="noopener">the canonical German maths-press write-up</a>
            of his most famous work — Gerd Fischer's 1999 essay in DMV-Mitteilungen doesn't even
            name him.
          </p>
          <p class="opening__close">
            Most of what follows happens between two places — <strong>Antwerp</strong>, where he
            was born, and <strong>Miseglia</strong>, where he stayed. The geography section that
            comes next draws the line between them on a map. This is a biography about staying in
            one place.
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.opening { background: var(--paper); }
.opening__inner { max-width: 1180px; margin: 0 auto; }
.opening__lede {
  font-family: var(--font-body);
  font-size: clamp(1.3rem, 1.8vw, 1.55rem);
  line-height: 1.33;
  color: #000;
  font-weight: 700;
  font-style: normal;
  margin: 0 0 3rem;
  letter-spacing: 0;
  max-width: 64ch;
}
.opening__lede .italic { font-style: italic; }

.opening__split {
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr);
  gap: 3rem;
  margin-bottom: 3rem;
  align-items: start;
}
.opening__split--reverse { grid-template-columns: minmax(0, 1fr) minmax(0, 1.5fr); }

.opening__col p {
  font-family: var(--font-display);
  font-size: clamp(1.2rem, 1.55vw, 1.5rem);
  line-height: 1.33;
  letter-spacing: 0.01em;
  color: #000;
  font-weight: 400;
  font-variant-numeric: oldstyle-nums proportional-nums;
  margin: 0 0 1.2em;
}
.opening__col p:last-child { margin-bottom: 0; }
.opening__col strong { color: #000; font-weight: 700; }
.opening__col .italic { font-style: italic; }
.opening__close {
  padding-top: 1.6rem;
  border-top: 1px solid var(--vein);
}

.opening__aside { position: sticky; top: 4rem; display: grid; gap: 0.6rem; }
.opening__aside-img {
  width: 100%;
  height: auto;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: 4px;
  filter: contrast(1.02);
}
.opening__aside-cap {
  font-family: var(--font-body);
  font-size: 1rem;
  line-height: 1.5;
  color: #000;
  opacity: 0.6;
  font-weight: 400;
}
.opening__aside-cap .muted { font-size: 0.78rem; letter-spacing: 0.05em; }

@media (max-width: 900px) {
  .opening__split, .opening__split--reverse { grid-template-columns: 1fr; }
  .opening__aside { position: static; }
}
</style>
